import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, ArrowLeft, House, ShieldCheck } from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsentSign() {
  const { regId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);

  const [accepted, setAccepted] = useState(false);
  const [clausesAccepted, setClausesAccepted] = useState({}); // { title: true }
  const [fullName, setFullName] = useState("");
  const [rut, setRut] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [guardianRut, setGuardianRut] = useState("");
  const [emerName, setEmerName] = useState("");
  const [emerPhone, setEmerPhone] = useState("");
  const [emerRel, setEmerRel] = useState("");
  const [signature, setSignature] = useState("");
  const sigRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/event-registrations/${regId}/consent-status`);
        setStatus(data);
        setFullName(data.full_name || "");
        setRut(data.rut || "");
        setAlreadySigned(!!data.already_signed);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Inscripción no encontrada");
      } finally {
        setLoading(false);
      }
    })();
  }, [regId]);

  if (loading) {
    return <main className="pt-20 min-h-screen flex items-center justify-center px-6"><p className="text-gray-400">Cargando...</p></main>;
  }
  if (!status) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-black uppercase mb-2">Inscripción no encontrada</h1>
          <p className="text-gray-400 mb-6">Revisa el link recibido por email.</p>
          <Link to="/"><Button>Ir al inicio</Button></Link>
        </div>
      </main>
    );
  }

  const isMinor = (status.age || 0) > 0 && status.age < 18;

  if (alreadySigned) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center px-6 py-10" data-testid="consent-already-signed">
        <div className="max-w-xl w-full text-center bg-gradient-to-b from-[#0F1F13] to-[#0A0A0A] border border-green-500/40 p-8">
          <CheckCircle size={72} weight="fill" className="text-green-400 mx-auto mb-4" />
          <h1 className="text-3xl font-black uppercase font-['Cabinet_Grotesk'] mb-2">Ya firmaste ✓</h1>
          <p className="text-gray-300 mb-1">Tu consentimiento para <b>{status.event_title}</b> — {status.distance_label} ya fue registrado.</p>
          <p className="text-xs text-gray-500 mb-6">Firmado el {(status.consent_signed_at || "").slice(0, 16).replace("T", " ")}</p>
          <Link to="/"><Button className="bg-[#FF4D00] hover:bg-[#E64500]"><House size={16} className="mr-2" /> Ir al inicio</Button></Link>
        </div>
      </main>
    );
  }

  const submit = async () => {
    // Extraer firma del canvas si no hay onChange todavía
    const sig = signature || (sigRef.current ? sigRef.current.exportPng() : "");
    const hasClauses = (status.consent_clauses || []).length > 0;
    if (hasClauses) {
      const required = (status.consent_clauses || []).map(c => (c.title || "").trim()).filter(Boolean);
      const missing = required.filter(t => !clausesAccepted[t]);
      if (missing.length > 0) { toast.error("Falta aceptar: " + missing.join(", ")); return; }
    } else {
      if (!accepted) { toast.error("Debes aceptar el consentimiento"); return; }
    }
    if (!sig) { toast.error("Por favor firma en el recuadro"); return; }
    if (!fullName.trim() || !rut.trim()) { toast.error("Nombre completo y RUT son obligatorios"); return; }
    if (!emerName.trim() || !emerPhone.trim()) { toast.error("Completa el contacto de emergencia"); return; }
    if (isMinor && (!guardianName.trim() || !guardianRut.trim())) {
      toast.error("Como menor de edad, debes completar los datos del apoderado");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/event-registrations/${regId}/sign-consent`, {
        consent_accepted: !hasClauses,
        consent_clauses_accepted: hasClauses ? Object.keys(clausesAccepted).filter(k => clausesAccepted[k]) : [],
        consent_signature: sig,
        consent_full_name_typed: fullName.trim(),
        consent_rut_typed: rut.trim(),
        consent_guardian_name: guardianName.trim(),
        consent_guardian_rut: guardianRut.trim(),
        emergency_contact_name: emerName.trim(),
        emergency_contact_phone: emerPhone.trim(),
        emergency_contact_relation: emerRel.trim(),
      });
      toast.success("¡Consentimiento firmado! Te enviamos la copia por email.");
      setStatus(prev => ({ ...(prev || {}), consent_signed_at: (data && data.signed_at) || new Date().toISOString(), already_signed: true }));
      setAlreadySigned(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al firmar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-20 min-h-screen bg-[#050505] px-4 py-10" data-testid="consent-sign-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center gap-2">
          <ShieldCheck size={22} className="text-[#FF4D00]" />
          <h1 className="text-2xl sm:text-3xl font-black uppercase font-['Cabinet_Grotesk']" data-testid="consent-title">Consentimiento Informado</h1>
        </div>
        <p className="text-gray-400 text-sm mb-6">
          <b>{status.event_title}</b> — {status.distance_label} · Participante: <b>{status.full_name}</b>
        </p>

        <div className="bg-[#0F0F0F] border border-white/10 p-5 mb-6" data-testid="consent-text-block">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">Lee con atención</div>
          {(status.consent_text || "").trim() && (
            <div className="whitespace-pre-wrap text-gray-300 text-sm leading-relaxed max-h-48 overflow-y-auto pr-2 mb-3" data-testid="consent-text">
              {status.consent_text}
            </div>
          )}
          {(status.consent_clauses || []).length > 0 && (
            <div className="space-y-2" data-testid="consent-clauses">
              {(status.consent_clauses || []).map((cl, idx) => {
                const title = (cl.title || "").trim();
                if (!title) return null;
                const checked = !!clausesAccepted[title];
                return (
                  <label
                    key={idx}
                    className={`block cursor-pointer p-3 border transition-colors ${checked ? "bg-[#FF4D00]/10 border-[#FF4D00]/50" : "bg-[#1A1A1A] border-white/10 hover:border-white/20"}`}
                    data-testid={`consent-clause-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => setClausesAccepted(p => ({ ...p, [title]: e.target.checked }))}
                        className="mt-0.5 w-5 h-5 accent-[#FF4D00] shrink-0"
                        data-testid={`consent-clause-checkbox-${idx}`}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-white">{title}</div>
                        {(cl.text || "").trim() && (
                          <div className="text-xs text-gray-400 whitespace-pre-wrap mt-1 leading-relaxed">{cl.text}</div>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          {!(status.consent_text || "").trim() && !((status.consent_clauses || []).length > 0) && (
            <div className="text-gray-500 text-sm">(Este evento no tiene texto de consentimiento configurado.)</div>
          )}
        </div>

        {isMinor && (
          <div className="bg-amber-500/10 border border-amber-500/40 p-4 mb-4">
            <div className="text-amber-300 font-bold text-sm mb-1">Participante menor de edad</div>
            <p className="text-gray-300 text-xs">
              Debes completar los datos del apoderado o tutor legal.
            </p>
          </div>
        )}

        {/* Firmante */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-500">Nombre completo *</label>
            <input value={fullName} onChange={e => setFullName(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 h-11 px-3 text-sm text-white focus:border-[#FF4D00] outline-none" data-testid="consent-input-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-500">RUT *</label>
            <input value={rut} onChange={e => setRut(e.target.value)} placeholder="12.345.678-9" className="w-full bg-[#0F0F0F] border border-white/10 h-11 px-3 text-sm text-white focus:border-[#FF4D00] outline-none" data-testid="consent-input-rut" />
          </div>
        </div>

        {isMinor && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-500">Apoderado *</label>
              <input value={guardianName} onChange={e => setGuardianName(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 h-11 px-3 text-sm text-white focus:border-[#FF4D00] outline-none" data-testid="consent-input-guardian-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-500">RUT apoderado *</label>
              <input value={guardianRut} onChange={e => setGuardianRut(e.target.value)} placeholder="12.345.678-9" className="w-full bg-[#0F0F0F] border border-white/10 h-11 px-3 text-sm text-white focus:border-[#FF4D00] outline-none" data-testid="consent-input-guardian-rut" />
            </div>
          </div>
        )}

        {/* Emergencia */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="consent-emergency-block">
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-500">Contacto emergencia *</label>
            <input value={emerName} onChange={e => setEmerName(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 h-11 px-3 text-sm text-white focus:border-[#FF4D00] outline-none" data-testid="consent-input-emer-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-500">Teléfono *</label>
            <input value={emerPhone} onChange={e => setEmerPhone(e.target.value)} placeholder="+56 9 ..." className="w-full bg-[#0F0F0F] border border-white/10 h-11 px-3 text-sm text-white focus:border-[#FF4D00] outline-none" data-testid="consent-input-emer-phone" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-500">Parentesco</label>
            <input value={emerRel} onChange={e => setEmerRel(e.target.value)} placeholder="Padre, Madre, Pareja..." className="w-full bg-[#0F0F0F] border border-white/10 h-11 px-3 text-sm text-white focus:border-[#FF4D00] outline-none" data-testid="consent-input-emer-rel" />
          </div>
        </div>

        {/* Firma dibujada */}
        <div className="mb-4">
          <SignaturePad ref={sigRef} label="Firma dibujada *" onChange={setSignature} data-testid="consent-signature" />
        </div>

        {/* Checkbox global (solo si NO hay cláusulas) */}
        {(status.consent_clauses || []).length === 0 && (
          <label className="flex items-start gap-2 mb-6 cursor-pointer select-none" data-testid="consent-accept-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[#FF4D00]"
              data-testid="consent-accept-checkbox"
            />
            <span className="text-sm text-gray-300">
              He leído y acepto el consentimiento informado. Declaro bajo juramento que la información entregada es verdadera y firmo electrónicamente.
            </span>
          </label>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-12 px-6"
            data-testid="consent-submit-btn"
          >
            {submitting ? "Firmando..." : "Firmar y enviar"}
          </Button>
          <Button variant="outline" onClick={() => navigate("/")} className="border-white/20 text-white hover:bg-white/5 h-12 px-6">
            <ArrowLeft size={16} className="mr-2" /> Cancelar
          </Button>
        </div>
      </div>
    </main>
  );
}
